// SOVRANOVA — "Talk to someone" panel. Surfaces on amber/red careTier from the
// Stepped-Care engine. Encourages PROFESSIONAL support (distinct from the
// always-on crisis lifeline). Calm, non-alarming, dismissible, region-aware,
// and fully offline-safe (reuses the bundled crisisResources, zero network).
import { useState } from 'react'
import { resolveRegion, getCrisisResources, HELPLINE_DIRECTORY_URL } from '../../services/crisisResources'
import { getProfessionalResources } from '../../services/professionalResources'

export function TalkToSomeone({ tier, exchanges = 99 }) {
  const [dismissed, setDismissed] = useState(false)
  // Collapsed by DEFAULT. This used to open as a full referral panel the moment
  // a session started, because the tier comes from stored PHQ-9/GAD-7 scores and
  // not from anything the person just said. The result was that someone opened
  // a therapy session, said one sentence, and the largest thing on screen was a
  // list of other services — covering the companion's actual reply. That is the
  // opposite of the agreed position (26 Jul): green is the default for all
  // therapeutic work, and escalation is THE USER'S choice to make.
  //
  // So: it waits until a real conversation has happened, then offers itself as
  // one quiet line the person can open if they want it. The always-on Urgent
  // help pill is untouched and remains one tap away on every screen — this
  // component is about ongoing professional support, never about crisis.
  const [open, setOpen] = useState(false)

  if (tier !== 'amber' && tier !== 'red') return null
  if (dismissed) return null
  // Never on the opening exchanges. Nobody is helped by being handed a directory
  // before they have finished their first thought.
  if (exchanges < 6) return null

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} style={S.quiet}>
        ♥ If you'd like, I can show you ways to find professional support
      </button>
    )
  }

  const region = resolveRegion()
  const res = getCrisisResources(region)
  const dirs = getProfessionalResources(region)
  const red = tier === 'red'

  return (
    <div style={red ? S.cardRed : S.cardAmber} role="note" aria-label="Support suggestion">
      <div style={S.row}>
        <span aria-hidden="true" style={S.dot}>♥</span>
        <p style={S.lead}>
          {red
            ? "What you're carrying sounds heavy — and you don't have to hold it alone. It really can help to have a professional alongside you."
            : "If things have felt like a lot lately, talking it through with a professional can help. No pressure — just an option that's here."}
        </p>
        <button type="button" onClick={() => setDismissed(true)} style={S.close} aria-label="Dismiss">×</button>
      </div>
      <div style={S.actions}>
        <span style={S.hint}>A good first step is your doctor (GP) or a therapist:</span>
        {dirs.map(d => (
          <a key={d.url} href={d.url} target="_blank" rel="noopener noreferrer" style={S.link}>
            {d.label} ↗
          </a>
        ))}
        <span style={S.hint}>Or to talk with someone right now:</span>
        {res.primary.tel
          ? <a href={`tel:${res.primary.tel}`} style={S.call}
               aria-label={`Call ${res.primary.label} on ${res.primary.number}`}>
              {res.primary.label} · {res.primary.number}
            </a>
          : <a href={HELPLINE_DIRECTORY_URL} target="_blank" rel="noopener noreferrer" style={S.call}
               aria-label={res.primary.label}>
              {res.primary.label} ↗
            </a>}
      </div>
    </div>
  )
}

const cardBase = {
  flexShrink: 0,
  borderRadius: '0.85rem',
  padding: '0.7rem 0.8rem',
  margin: '0.4rem 0 0.2rem',
  display: 'flex', flexDirection: 'column', gap: '0.45rem',
  color: '#3a2f1a',
}
const S = {
  // The collapsed state: one quiet line, not a panel. It should read as an offer
  // sitting to one side of the conversation, never as an interruption of it.
  quiet: { flexShrink: 0, alignSelf: 'flex-start', background: 'transparent', border: 'none',
           color: 'rgba(58,47,26,0.6)', fontSize: '0.78rem', fontFamily: 'inherit',
           cursor: 'pointer', padding: '0.35rem 0.1rem', margin: '0.2rem 0', textAlign: 'left' },
  cardAmber: { ...cardBase, background: 'rgba(201,162,39,0.10)', border: '1px solid rgba(201,162,39,0.45)' },
  cardRed:   { ...cardBase, background: 'rgba(201,162,39,0.16)', border: '1px solid rgba(201,162,39,0.7)' },
  row:   { display: 'flex', alignItems: 'flex-start', gap: '0.5rem' },
  dot:   { color: '#c9a227', fontSize: '0.95rem', lineHeight: 1.4, flexShrink: 0 },
  lead:  { margin: 0, fontSize: '0.86rem', lineHeight: 1.5, flex: 1 },
  close: { background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1, color: 'rgba(58,47,26,0.55)', padding: '0 0.15rem', flexShrink: 0 },
  actions: { display: 'flex', flexDirection: 'column', gap: '0.35rem', paddingLeft: '1.45rem' },
  hint:  { fontSize: '0.76rem', color: 'rgba(58,47,26,0.7)', lineHeight: 1.45 },
  link:  { alignSelf: 'flex-start', textDecoration: 'none', fontSize: '0.83rem', fontWeight: 600, color: '#6b5310', borderBottom: '1px solid rgba(107,83,16,0.4)', paddingBottom: '1px' },
  call:  { alignSelf: 'flex-start', textDecoration: 'none', fontSize: '0.85rem', fontWeight: 700, color: '#7a5d12', background: 'rgba(201,162,39,0.18)', border: '1px solid rgba(201,162,39,0.55)', borderRadius: '0.6rem', padding: '0.35rem 0.65rem' },
}
